import { useMemo } from 'react';

type ProjectPermissionSource =
  | { permissions?: string[] | null }
  | readonly string[]
  | null
  | undefined;

export interface ProjectPermissionsResult<TPermission extends string> {
  permissions: Record<TPermission, boolean>;
  hasPermission: (permission: TPermission) => boolean;
  hasAnyPermission: (permissions: readonly TPermission[]) => boolean;
  hasAllPermissions: (permissions: readonly TPermission[]) => boolean;
}

const getPermissionList = (source: ProjectPermissionSource): readonly string[] => {
  if (!source) return [];
  if (Array.isArray(source)) return source;
  return (source as { permissions?: string[] | null }).permissions ?? [];
};

export const useProjectPermissions = <const TPermissions extends readonly string[]>(
  source: ProjectPermissionSource,
  requestedPermissions: TPermissions,
): ProjectPermissionsResult<TPermissions[number]> => {
  return useMemo(() => {
    const granted = new Set(getPermissionList(source));
    const checkedPermissions = {} as Record<TPermissions[number], boolean>;

    for (const permission of requestedPermissions as readonly TPermissions[number][]) {
      checkedPermissions[permission] = granted.has(permission);
    }

    const hasPermission = (permission: TPermissions[number]) => checkedPermissions[permission] === true;

    return {
      permissions: checkedPermissions,
      hasPermission,
      hasAnyPermission: (permissions: readonly TPermissions[number][]) =>
        permissions.some((permission) => hasPermission(permission)),
      hasAllPermissions: (permissions: readonly TPermissions[number][]) =>
        permissions.every((permission) => hasPermission(permission)),
    };
  }, [source, requestedPermissions]);
};
